import { Button, type baseButtonVariants } from "@spz/ui/components/button";
import { Icon } from "@spz/ui/components/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@spz/ui/components/popover";
import { cn } from "@spz/ui/lib/utils";
import type { VariantProps } from "class-variance-authority";
import { type ComponentPropsWithoutRef, useState } from "react";

type ButtonVariants = VariantProps<typeof baseButtonVariants>["variant"];

export interface DropdownTriggerProps
  extends Omit<ComponentPropsWithoutRef<"div">, "onChange"> {
  value?: string;
  defaultValue?: string;
  buttonVariant?: ButtonVariants;
  options: {
    label: string;
    value: string;
    description?: string;
    disabled?: boolean;
  }[];
  disabled?: boolean;
  onChange?: (value: string) => void;
  placeholder?: string;
  buttonClassName?: string;
  alignDropdownContent?: "start" | "end" | "center";
}

export interface DropdownTriggerComponentProps extends DropdownTriggerProps {
  ref?: React.Ref<HTMLDivElement>;
}

export const DropdownTrigger: React.FC<DropdownTriggerComponentProps> = ({
  className,
  value,
  defaultValue,
  options,
  buttonVariant = "ghost",
  placeholder = "",
  disabled = false,
  onChange,
  buttonClassName,
  alignDropdownContent = "center",
  ref,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);

  const currentValue = value !== undefined ? value : internalValue;
  const selectedOption = options.find((opt) => opt.value === currentValue);

  const handleSelect = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    setIsOpen(false);
    onChange?.(newValue);
  };

  return (
    <div ref={ref} className={cn("relative w-full", className)} {...props}>
      {/* Only render Popover if not disabled */}
      {disabled ? (
        <Button
          variant={buttonVariant}
          className="p-0 inline-flex items-center justify-between"
          disabled
          aria-disabled
          childrenClassName="flex gap-0.5 pl-2 pr-1"
        >
          <span
            className={cn(
              buttonVariant === "primary" ? "text-white" : "text-gray-900",
              "text-caption font-title truncate flex-1 text-left",
            )}
          >
            {selectedOption?.label || placeholder}
          </span>
          <Icon icon="chevron-down" className="shrink-0" size={16} />
        </Button>
      ) : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={buttonVariant}
              className={cn(
                "p-0 inline-flex items-center justify-between",
                buttonClassName,
              )}
              aria-expanded={isOpen}
              childrenClassName="flex gap-0.5 pl-2 pr-1"
            >
              <span
                className={cn(
                  buttonVariant === "primary" ? "text-white" : "text-gray-900",
                  "text-caption font-title truncate flex-1 text-left",
                )}
              >
                {selectedOption?.label || placeholder}
              </span>
              <Icon
                icon="chevron-down"
                className={cn(
                  "shrink-0 transition-transform duration-200",
                  isOpen && "rotate-180 text-primary",
                )}
                size={16}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            sideOffset={3}
            className="p-0 min-w-[200px] max-w-[244px] border"
            align={alignDropdownContent}
          >
            <div className="flex flex-col w-full">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "group relative flex items-start px-2 py-1",
                    !option.disabled && "hover:bg-gray-50 cursor-pointer",
                  )}
                  onClick={() => handleSelect(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(option.value);
                    }
                  }}
                  aria-selected={currentValue === option.value}
                >
                  <div className="flex flex-col flex-1 min-w-0 pr-8">
                    <span className="text-caption font-title text-gray-900 truncate">
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="mt-1 text-caption font-base text-gray-700 break-words">
                        {option.description}
                      </span>
                    )}
                  </div>

                  {currentValue === option.value && (
                    <div className="absolute inset-y-1/2 right-2 flex items-center justify-center">
                      <Icon
                        icon="check"
                        className="text-primary shrink-0"
                        size={20}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

DropdownTrigger.displayName = "DropdownTrigger";
